import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Eye, EyeOff, Shield, Building2, ArrowLeft } from "lucide-react";
import { useUser, User } from "./UserContext";
import axios from "axios";
import { toast } from "sonner";
// import courierLogo from 'figma:asset/fad5c0c98971d01ba354f23784cde20752b79528.png';

import { jwtDecode, JwtPayload } from "jwt-decode";
import { useDispatch } from "react-redux";
import { loginUser } from "@/redux/slices/auth/authSlice";
import axiosInstance from "@/api/axiosInstance";
import { useNavigate } from "react-router-dom";
import GlobalLoader from "./ui/GlobalLoader";

interface LoginProps {
  onLogin: () => void;
  onNavigateToSignup: () => void;
  onNavigateToForgotPassword: () => void;
  userType?: 'pharmacy' | 'admin';
}

// Mock user database
// const mockUsers: User[] = [
//   {
//     id: "1",
//     email: "admin@courier-rx.com",
//     firstName: "System",
//     lastName: "Administrator",
//     role: "super_admin",
//     permissions: ["all"],
//     lastLogin: "2025-08-20 08:30",
//     isActive: true,
//   },
//   {
//     id: "2",
//     email: "sarah.wilson@centralpharmacy.com",
//     firstName: "Sarah",
//     lastName: "Wilson",
//     role: "pharmacy_admin",
//     title: "PharmD",
//     pharmacyId: "1",
//     pharmacyName: "Central Pharmacy",
//     permissions: [
//       "view_orders",
//       "manage_orders",
//       "request_pickup",
//       "view_analytics",
//     ],
//     lastLogin: "2025-08-20 09:15",
//     isActive: true,
//   },
//   {
//     id: "3",
//     email: "mike.chen@medcarepharmacy.com",
//     firstName: "Mike",
//     lastName: "Chen",
//     role: "pharmacy_admin",
//     title: "PharmD",
//     pharmacyId: "2",
//     pharmacyName: "MedCare Pharmacy",
//     permissions: [
//       "view_orders",
//       "manage_orders",
//       "request_pickup",
//       "view_analytics",
//     ],
//     lastLogin: "2025-08-20 07:45",
//     isActive: true,
//   },
// ];

export function Login({
  onLogin,
  onNavigateToSignup,
  onNavigateToForgotPassword,
  userType = 'pharmacy',
}: LoginProps) {
  const { setUser } = useUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginUserType, setLoginUserType] = useState<"super_admin" | "pharmacy">(
    userType === 'admin' ? "super_admin" : "pharmacy"
  );

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const baseURL = import.meta.env.VITE_API_BASE_URL;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setIsLoading(true);
  //   setError("");

  //   // Simulate API call
  //   setTimeout(() => {
  //     // Find user by email and validate credentials
  //     const user = mockUsers.find((u) => u.email === email);

  //     if (!user) {
  //       setError("Invalid email or password");
  //       setIsLoading(false);
  //       return;
  //     }

  //     // Check if user type matches
  //     if (userType === "super_admin" && user.role !== "super_admin") {
  //       setError("Invalid credentials for Super Admin access");
  //       setIsLoading(false);
  //       return;
  //     }

  //     if (userType === "pharmacy" && user.role === "super_admin") {
  //       setError("Super Admin accounts cannot access Pharmacy Portal");
  //       setIsLoading(false);
  //       return;
  //     }

  //     if (!user.isActive) {
  //       setError("Account is deactivated. Please contact support.");
  //       setIsLoading(false);
  //       return;
  //     }

  //     // Update last login time
  //     user.lastLogin = new Date().toISOString();

  //     setUser(user);
  //     setIsLoading(false);
  //     onLogin();
  //   }, 1000);
  // };

  // Quick login buttons for demo

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError("");
      
      const payload = { 
        email, 
        password, 
        userType: userType === 'admin' ? 'admin' : 'pharmacy'
      };
      const res = await dispatch<any>(loginUser(payload)).unwrap();
      if(res.data.isVerified) {
        console.log(res.data.message)
      }
      navigate("/dashboard");

    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data?.message || "Incorrect credentials.");
        console.log(error.response.data?.message || "Incorrect credentials.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // const handleQuickLogin = (userEmail: string) => {
  //   setEmail(userEmail);
  //   setPassword("demo123");
  //   const user = mockUsers.find((u) => u.email === userEmail);
  //   if (user) {
  //     setUserType(user.role === "super_admin" ? "super_admin" : "pharmacy");
  //   }
  // };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header with Back to Home */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Button>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                {userType === 'admin' ? (
                  <Shield className="w-6 h-6 text-white" />
                ) : (
                  <Building2 className="w-6 h-6 text-white" />
                )}
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Welcome to Courier-RX
            </h1>
            <p className="text-gray-600 mt-2">
              Enterprise Pharmacy Delivery Platform
            </p>
          </div>
          
          <div className="w-24"></div> {/* Spacer for centering */}
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-center text-xl flex items-center justify-center space-x-2">
              {userType === 'admin' ? (
                <>
                  <Shield className="w-5 h-5 text-green-600" />
                  <span>Admin Login</span>
                </>
              ) : (
                <>
                  <Building2 className="w-5 h-5 text-green-600" />
                  <span>Pharmacy Login</span>
                </>
              )}
            </CardTitle>
            <p className="text-center text-sm text-gray-600">
              {userType === 'admin' 
                ? 'Access your Admin Dashboard' 
                : 'Access your Pharmacy Dashboard'
              }
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={userType === 'admin' ? 'Enter your admin email' : 'Enter your pharmacy email'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-600">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={onNavigateToForgotPassword}
                  className="text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-green-600 hover:bg-green-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  `Sign In as ${userType === 'admin' ? 'Admin' : 'Pharmacy'}`
                )}
              </Button>
            </form>

            <Separator className="my-6" />

            <div className="text-center mt-6">
              <p className="text-gray-600">
                {userType === 'admin' ? (
                  <>
                    Need admin access?{" "}
                    <span className="text-gray-500">Contact System Administrator</span>
                  </>
                ) : (
                  <>
                    Need pharmacy access?{" "}
                    <button
                      onClick={onNavigateToSignup}
                      className="text-green-600 hover:text-green-700 font-medium"
                    >
                      Request Account
                    </button>
                  </>
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            © 2025 Courier-RX. Enterprise Pharmacy Delivery Platform.
          </p>
        </div>
      </div>

      {/* Global loading overlay */}
      <GlobalLoader 
        isLoading={isLoading}
        message="Signing in..."
        subMessage="Please wait while we authenticate your account"
      />
    </div>
  );
}
